import { Injectable, QueryList } from '@angular/core';
import { Tooltip } from 'primeng/tooltip';
import { debounce } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class TooltipWatchdogService {
  private tooltips: QueryList<Tooltip> | undefined;

  /**
   * Debounced cleanup function to hide all tooltips managed by this watchdog
   */
  private readonly hideDebounced = debounce(() => this.hideAllTooltips(), 10);

  /**
   * Registers the tooltips to be managed. 
   * Call this in the component containing the tooltips, usually passing the @ViewChildren(Tooltip).
   */
  registerTooltips(tooltips: QueryList<Tooltip>) {
    this.tooltips = tooltips;
  }

  /**
   * Main interaction handler. Should be called by a component's @HostListener('mouseover', ['$event'])
   */
  handleMouseOver(event: MouseEvent) {
    if (!this.tooltips || this.tooltips.length === 0) return;

    const target = event.target as HTMLElement;
    if (!target) return;

    // Use managed tooltips as the source of truth for "Safe Zones"
    const matchingTooltip = this.tooltips.find((t) =>
      t.el.nativeElement === target || t.el.nativeElement.contains(target)
    );

    if (matchingTooltip) {
      // If moving between icons quickly, deactivate others immediately to prevent overlap
      this.tooltips.forEach((t) => {
        if (t !== matchingTooltip && ((t as any).container || (t as any).timeout)) {
          t.deactivate();
        }
      });

      // Force activation if PrimeNG's state is stuck (e.g. missed mouseenter)
      const internal = matchingTooltip as any;
      if (!internal.container && !internal.timeout) {
        matchingTooltip.activate();
      }
    } else if (!target.classList.contains('p-tooltip') && !target.closest('.p-tooltip')) {
      // Move outside host and tooltip container -> cleanup
      this.hideDebounced();
    }
  }

  /**
   * Programmatic hide all tooltips
   */
  hideAllTooltips() {
    if (this.tooltips && this.tooltips.length > 0) {
      this.tooltips.forEach((tooltip) => {
        const t = tooltip as any;
        if (t && (t.container || t.timeout)) {
          tooltip.deactivate();
        }
      });
    }
  }

  /**
   * Clean up debonced functions if necessary
   */
  cleanup() {
    this.hideDebounced.cancel();
    this.tooltips = undefined;
  }
}
